import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Coffee, ShoppingBag, ConciergeBell, BadgeCheck, Tag, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, fmtNum } from "../components/common";

const TIERS = [
  { name: "Free Listing", price: "Free", features: ["Business name & category", "Airport + terminal location", "Phone & website", "Basic description"], cta: "Claim your business", highlight: false },
  { name: "Premium Profile", price: "Coming soon", features: ["Photo gallery & logo", "Menu / product catalog", "Publish deals & coupons", "Featured placement & analytics"], cta: "Join the waitlist", highlight: true },
  { name: "Custom Website", price: "Coming soon", features: ["TravelerPulse-hosted site", "SEO & airport location map", "Online inquiries & reviews", "Multi-airport brand support"], cta: "Join the waitlist", highlight: false },
];

export default function Marketplace() {
  const [summary, setSummary] = useState(null);
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/marketplace/airports-summary").then((r) => setSummary(r.data)).catch(() => setSummary({ items: [], active_deals: 0 }));
    api.get("/marketplace/deals").then((r) => setDeals(r.data.items)).catch(() => {});
  }, []);

  const totals = summary?.items?.reduce((a, i) => ({ total: a.total + i.total, verified: a.verified + i.verified }), { total: 0, verified: 0 });

  return (
    <div data-testid="marketplace-page">
      <PageHeader eyebrow="Airport Marketplace" title="Dining, Shopping & Services at the Airport"
        subtitle="Every airport is a miniature city of restaurants, coffee shops, retailers and services. Discover them by terminal — and if it's your business, claim it." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        {!summary ? <Spinner /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300 mb-10">
              {[["Businesses", fmtNum(totals.total)], ["Airports covered", summary.items.length],
                ["Verified businesses", fmtNum(totals.verified)], ["Active deals", fmtNum(summary.active_deals)]].map(([l, v]) => (
                <div key={l} className="bg-white px-5 py-4">
                  <div className="font-heading font-900 text-2xl sm:text-3xl text-primary">{v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mt-0.5">{l}</div>
                </div>
              ))}
            </div>

            <h2 className="font-heading font-900 text-xl sm:text-2xl mb-4 flex items-center gap-2">
              <Store className="w-6 h-6 text-primary" /> Browse by airport
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
              {summary.items.map((a) => (
                <Link key={a.iata} to={`/airports/${a.iata}?tab=shops`} data-testid={`marketplace-airport-${a.iata}`}
                  className="group border border-zinc-300 bg-white p-5 hover:border-primary hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-700 text-lg text-primary">{a.iata}</span>
                    <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                  <div className="font-heading font-900 text-lg mt-1 leading-snug">{a.airport_name}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Coffee className="w-3.5 h-3.5 text-orange-600" /> {a.food} dining</span>
                    <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5 text-blue-600" /> {a.retail} retail</span>
                    <span className="inline-flex items-center gap-1"><ConciergeBell className="w-3.5 h-3.5 text-teal-600" /> {a.services} services</span>
                  </div>
                </Link>
              ))}
            </div>

            {deals.length > 0 && (
              <>
                <h2 className="font-heading font-900 text-xl sm:text-2xl mb-4 flex items-center gap-2">
                  <Tag className="w-6 h-6 text-emerald-600" /> Traveler deals
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
                  {deals.slice(0, 6).map((d) => (
                    <Link key={d.id} to={`/business/${d.business_id}`} className="border border-emerald-300 bg-emerald-50 p-4 hover:shadow-lg transition-shadow" data-testid={`deal-card-${d.id}`}>
                      <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">{d.airport_iata}{d.terminal ? ` · ${d.terminal}` : ""} · Deal</div>
                      <div className="font-heading font-900 text-lg mt-1">{d.title}</div>
                      <div className="text-sm text-muted-foreground mt-1">{d.business_name}</div>
                    </Link>
                  ))}
                </div>
              </>
            )}

            <div className="border border-emerald-800 bg-emerald-800 text-white p-6 sm:p-8 mb-12" data-testid="claim-cta">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-400">
                <BadgeCheck className="w-4 h-4" /> For airport businesses
              </div>
              <h2 className="font-heading font-900 text-2xl sm:text-3xl mt-2">Is this your business? Claim your free profile.</h2>
              <p className="text-zinc-300 mt-2 max-w-2xl">Find your store or restaurant in the directory, hit "Claim this business", and after verification you can update hours, contact details, descriptions — and publish deals travelers see right on the airport page.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {summary.items.slice(0, 6).map((a) => (
                  <Link key={a.iata} to={`/airports/${a.iata}?tab=shops`} className="bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 text-sm font-700">{a.iata}</Link>
                ))}
              </div>
            </div>

            <h2 className="font-heading font-900 text-xl sm:text-2xl mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-600" /> Marketplace plans
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {TIERS.map((t) => (
                <div key={t.name} className={`border p-6 ${t.highlight ? "border-primary shadow-lg" : "border-zinc-300 bg-white"}`} data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-heading font-900 text-xl">{t.name}</div>
                  <div className={`text-sm font-700 mt-1 ${t.highlight ? "text-primary" : "text-muted-foreground"}`}>{t.price}</div>
                  <ul className="mt-4 space-y-2 text-sm">
                    {t.features.map((f) => <li key={f} className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
